import React from 'react'
import Chip from '@material-ui/core/Chip'
import { makeStyles } from '@material-ui/core/styles'
import { primaryBlue, darkBlue, gray } from '../../../styles/material-ui-theme-v2'

export interface ProgressChipOption {
  label: React.ReactNode
  value: any
  disabled?: boolean
  /** Optional leading icon element, rendered before the label. */
  icon?: React.ReactElement
}

interface ProgressChipsProps {
  options: ProgressChipOption[]
  /** Selected value (single-select mode). */
  value?: any
  /** Selected values (multi-select mode). */
  values?: any[]
  /**
   * Fires with the clicked option's value. In multi-select mode the caller owns the toggle
   * logic (add/remove from `values`).
   */
  onChange: (value: any) => void
  ariaLabel: string
  style?: React.CSSProperties
  /** Renders the chips as independent on/off toggles (checkbox group) instead of a radiogroup. */
  multiSelect?: boolean
  /** Keeps the chips on one horizontally scrollable row instead of wrapping. */
  scroll?: boolean
}

const useStyles = makeStyles({
  group: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    marginTop: '10px',
  },
  groupScroll: {
    flexWrap: 'nowrap',
    overflowX: 'auto',
    // Content stays reachable by scrolling (WCAG 1.4.10 Reflow); hide the bar for a clean row.
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    // Bleed through the host's horizontal padding so the row clips at the page edge instead
    // of mid-padding: the Card pads 40px on desktop, .layout__content 16px(+safe area) below md.
    margin: '10px -40px 0',
    padding: '0 40px',
    '@media (max-width: 991px)': {
      marginLeft: 'calc(-1 * max(16px, var(--safe-area-inset-left)))',
      marginRight: 'calc(-1 * max(16px, var(--safe-area-inset-right)))',
      paddingLeft: 'max(16px, var(--safe-area-inset-left))',
      paddingRight: 'max(16px, var(--safe-area-inset-right))',
    },
  },
  chip: {
    margin: 0,
    // Never squish a chip to fit the row — in scroll mode the row scrolls instead.
    flexShrink: 0,
    //fontWeight: 600,
    fontWeight: 600,
    fontSize: '0.8125rem',
    height: '2rem',
    //borderRadius: '8px',
    backgroundColor: gray,
    color: darkBlue,
    transition: 'background-color 0.15s ease',
    /*'&:hover, &:focus': {
      backgroundColor: '#E3E3E8',
    },*/
    '&.Mui-disabled': {
      opacity: 0.5,
    },
    // Leading option icon follows the chip's text color (selected chips turn blue with it).
    // fill is set explicitly because the SF-symbol SVGs carry no fill attribute of their own.
    '& .MuiChip-icon': {
      width: '16px',
      height: '16px',
      color: 'inherit',
      fill: 'currentColor',
      marginLeft: '10px',
      marginRight: '-6px',
    },
  },
  chipSelected: {
    /*backgroundColor: primaryBlue,
    color: '#fff',
    '&:hover, &:focus': {
      backgroundColor: primaryBlue,
    },*/
    backgroundColor: '#fff',
    color: primaryBlue,
    borderColor: `${primaryBlue} !important`,
    '&:hover, &:focus': {
      backgroundColor: '#fff',
    },
  },
})

/**
 * Accessible chip group used as an alternative to the secondary progress tabs. Chips wrap
 * naturally onto multiple rows, so nothing truncates or hides at high browser zoom (WCAG 1.4.10
 * Reflow). Single-select by default (a proper radiogroup with roving tabindex and arrow-key
 * navigation); with `multiSelect` the chips become independent on/off toggles (a checkbox group
 * where arrow keys only move focus and Enter/Space toggles).
 */
export default function ProgressChips(props: ProgressChipsProps) {
  const { options, value, values, onChange, ariaLabel, style, multiSelect, scroll } = props
  const classes = useStyles()
  const refs = React.useRef<Array<HTMLDivElement | null>>([])
  const groupRef = React.useRef<HTMLDivElement | null>(null)

  // In scroll mode, an active chip can sit beyond the visible part of the row when the view
  // opens (e.g. a persisted filter) — invisible until the user happens to scroll. Center it
  // once when it first exists; the flag keeps later user interactions from re-jumping the row.
  const didAutoScrollRef = React.useRef(false)
  React.useLayoutEffect(() => {
    if (!scroll || didAutoScrollRef.current) return
    const container = groupRef.current
    const index = options.findIndex((opt) =>
      multiSelect ? !!values?.includes(opt.value) : opt.value !== undefined && opt.value === value,
    )
    const chip = index >= 0 ? refs.current[index] : null
    if (!container || !chip) return
    didAutoScrollRef.current = true
    const containerRect = container.getBoundingClientRect()
    const chipRect = chip.getBoundingClientRect()
    const chipLeft = chipRect.left - containerRect.left + container.scrollLeft
    const outOfView =
      chipLeft < container.scrollLeft || chipLeft + chipRect.width > container.scrollLeft + container.clientWidth
    if (outOfView) {
      // Direct scrollLeft (not scrollIntoView) so only the row moves, never the page.
      container.scrollLeft = chipLeft - (container.clientWidth - chipRect.width) / 2
    }
  }, [scroll, value, values, options, multiSelect])

  const enabled = options.filter((o) => !o.disabled)
  const isSelected = (opt: ProgressChipOption) => (multiSelect ? !!values?.includes(opt.value) : opt.value === value)
  const hasSelection = !multiSelect && options.some((o) => o.value === value && !o.disabled)
  // Element that holds tabindex=0 when nothing is selected yet (and always in multi-select,
  // where several chips can be on): the first enabled chip.
  const fallbackValue = enabled.length ? enabled[0].value : undefined

  const focusValue = (val: any) => {
    const idx = options.findIndex((o) => o.value === val)
    refs.current[idx]?.focus()
  }

  const onKeyDown = (event: React.KeyboardEvent, opt: ProgressChipOption) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onChange(opt.value)
      return
    }
    const dir =
      event.key === 'ArrowRight' || event.key === 'ArrowDown'
        ? 1
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
          ? -1
          : 0
    if (!dir || !enabled.length) return
    event.preventDefault()
    const current = enabled.findIndex((o) => o.value === opt.value)
    const next = enabled[(current + dir + enabled.length) % enabled.length]
    // Radios select on arrow navigation; checkboxes only move focus (toggling is Enter/Space).
    if (!multiSelect) onChange(next.value)
    focusValue(next.value)
  }

  return (
    <div
      ref={groupRef}
      role={multiSelect ? 'group' : 'radiogroup'}
      aria-label={ariaLabel}
      className={`${classes.group} ${scroll ? classes.groupScroll : ''}`}
      style={style}
    >
      {options.map((opt, index) => {
        const selected = isSelected(opt)
        const tabbable = opt.disabled ? false : hasSelection ? selected : opt.value === fallbackValue
        return (
          <Chip
            key={String(opt.value)}
            ref={(el: HTMLDivElement | null) => (refs.current[index] = el)}
            label={opt.label}
            icon={opt.icon}
            clickable={!opt.disabled}
            disabled={opt.disabled}
            onClick={() => onChange(opt.value)}
            onKeyDown={(event: React.KeyboardEvent) => onKeyDown(event, opt)}
            className={`${classes.chip} ${selected ? classes.chipSelected : ''}`}
            role={multiSelect ? 'checkbox' : 'radio'}
            aria-checked={selected}
            tabIndex={tabbable ? 0 : -1}
          />
        )
      })}
    </div>
  )
}
